import { LitElement, PropertyDeclaration } from 'lit-element';
import {
  onHtmlDirChange,
  offHtmlDirChange,
  getHtmlDir,
} from '../../utils/rtl-utils';
// this import is mocked when building for the asset loader
import '../../metrics/register-component-metrics';
import { coerceToBoolean, booleanConverter } from './type-utils';

/**
 * Our extension of LitElement. Fires an event when the component is connected,
 * and adds additional useful methods.
 */
export class KatLitElement extends LitElement {
  private _observers = [];

  // Used by sub classes to specify which properties to reflect onto a hidden input.
  // Expects a slot called "private-light-dom" to exist.
  protected _propsToReflectToHiddenInput = null;
  protected _extraLogicForHiddenInput = null;

  connectedCallback() {
    window.dispatchEvent(
      new CustomEvent('katal-component-connected', {
        detail: { element: this },
      })
    );

    this.__updateRtl(getHtmlDir());
    onHtmlDirChange(this.__updateRtl);

    super.connectedCallback();
  }

  disconnectedCallback() {
    offHtmlDirChange(this.__updateRtl);
    super.disconnectedCallback();
  }

  dispatchCustomEvent<T extends {}>(
    eventType: string,
    detail?: T,
    bubbles: Omit<CustomEventInit, 'detail'> | boolean = true
  ) {
    const evt = new CustomEvent(eventType, {
      detail,
      ...(typeof bubbles === 'boolean' ? { bubbles } : bubbles),
    });
    this.dispatchEvent(evt);
  }

  observeChildren(
    parentElement: HTMLElement,
    callback: () => void
  ): MutationObserver {
    const observer = new MutationObserver(callback);
    observer.observe(parentElement, { childList: true });
    this._observers.push(observer);
    return observer;
  }

  updated(changedProps) {
    if (!this._propsToReflectToHiddenInput) return;

    const anyChanged = this._propsToReflectToHiddenInput.some(prop =>
      changedProps.has(prop)
    );
    if (!anyChanged) return;

    let slot = this.querySelector("span[slot='private-light-dom']");
    if (!slot) {
      slot = document.createElement('span');
      slot.setAttribute('slot', 'private-light-dom');
      this.appendChild(slot);
    }

    let input = slot.getElementsByTagName('input')[0];
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      slot.appendChild(input);
    }

    this._propsToReflectToHiddenInput.forEach(prop => {
      let value = this[prop];
      // Handle bool values
      if (value === true) {
        value = '';
      } else if (value === false) {
        value = null;
      }

      if (prop === 'value') {
        input.value = value;
        return;
      }

      // null or undefined
      if (value == null) {
        input.removeAttribute(prop);
      } else {
        input.setAttribute(prop, value);
      }
    });

    if (this._extraLogicForHiddenInput) {
      this._extraLogicForHiddenInput(input);
    }
  }

  updateHiddenInput(name, value) {}

  // Override lit so we can coerce properties to the right types.
  // Lit only calls this if the class doesn't already have the descriptor defined.
  protected static getPropertyDescriptor(
    name: PropertyKey,
    key: string | symbol,
    options: PropertyDeclaration
  ) {
    const descriptor = super.getPropertyDescriptor(name, key, options);

    if (options.type === Boolean || options.converter === booleanConverter) {
      descriptor.set = createDescriptorSetter(name, key, coerceToBoolean);
    }

    return descriptor;
  }

  __updateRtl = val => {
    if (!val) {
      this.removeAttribute('dir');
    } else {
      this.setAttribute('dir', val);
    }
  };
}

function createDescriptorSetter<T>(
  name: PropertyKey,
  key: string | symbol,
  coerce: (val: unknown) => T
) {
  return function(value: unknown) {
    const oldValue = this[name];
    this[key] = coerce(value);
    this.requestUpdate(name, oldValue);
  };
}
